/**
 * Firebase initialization — ISOLATED module, used ONLY for Cloud Messaging (FCM).
 *
 * ⚠️ This does NOT touch Supabase. Supabase remains the primary backend for
 * auth / database / storage. Firebase here is exclusively for push notifications.
 *
 * All values are read from environment variables (Vite). They are the PUBLIC
 * Firebase web-app config (safe to expose on the client). Set them in a `.env`
 * file at the client root (see .env.example) — never hard-code them here.
 */

import { initializeApp, getApps, getApp } from 'firebase/app';

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// The Web Push certificate "key pair" (VAPID public key) from Firebase console:
// Project settings → Cloud Messaging → Web Push certificates.
export const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

/** True only when the minimum config needed for FCM is present. */
export const isFirebaseConfigured = () =>
  !!(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.messagingSenderId && firebaseConfig.appId);

/** Lazily initialise (or reuse) the Firebase app. Returns null if not configured. */
export const getFirebaseApp = () => {
  if (!isFirebaseConfigured()) return null;
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
};
